import { Box, Stack, Tab, Tabs } from '@mui/material'
import { getLoginPerOrgUserReport, getTotalEngagementPerOrgUserReport } from 'api/reports'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getTitle } from 'components/tabs/TabsTitle'
import { useCohortsFilter } from 'features/Cohorts'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useRefreshTable } from 'hooks/useRefreshTable'
import React, { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { BaseTableRequest, EngagementOrgUserReportTypes, FilterModel, UserTypeTab } from 'types'
import { getRoute, useGetDataSource } from 'utils'
import { useOrgRoleFilter } from '../../../User/hooks/useOrgRoleFilter'
import { prepareDateRangeParams } from '../../helpers/prepareDateRangeParams'
import { useAccessCodeFilter } from '../../hooks/useAccessCodeFilter'
import { useDateRange } from '../../hooks/useDateRange'
import { DateRangeToolbar } from '../DateRangeToolbar/DateRangeToolbar'
import { useReportsEngagementOrgUser } from './useReportsEngagementOrgUser'

export function ReportsEngagementOrgUser() {
  const navigate = useNavigate()
  const { engagementType } = useParams()

  const selectedTab = useMemo(() => {
    if (!engagementType) return EngagementOrgUserReportTypes.TotalEngagement

    return engagementType as EngagementOrgUserReportTypes
  }, [engagementType])

  const handleChangeSelectedTab = useCallback(
    (_: React.SyntheticEvent, newValue: EngagementOrgUserReportTypes) => {
      const route = getRoute(routes.reports_engagement_org_user, { engagementType: newValue })
      navigate(route)
    },
    [navigate],
  )

  const { RoleFilter, rolesFilterValue } = useOrgRoleFilter(UserTypeTab.ORG, 'userType')

  const { AccessCodeFilter, accessCodeFilterValue } = useAccessCodeFilter()

  const { cohortsFilterValue, CohortsFilter } = useCohortsFilter()

  const {
    rangeType,
    year,
    month,
    selectedDate,
    dateInterval,
    onChangeDateInterval,
    onChangeRange,
    onChangeYear,
    onChangeMonth,
    setSelectedDate,
  } = useDateRange()

  const getReportWrapper = useCallback(
    (params: BaseTableRequest) => {
      let extendFilter: FilterModel = params.filter || {}

      if (rolesFilterValue) {
        extendFilter = {
          ...extendFilter,
          ...rolesFilterValue,
        }
      }

      if (accessCodeFilterValue) {
        extendFilter.ownedAccessCodes = accessCodeFilterValue
      }

      if (cohortsFilterValue) {
        extendFilter.cohortId = cohortsFilterValue
      }

      const dateParams = prepareDateRangeParams(rangeType, year, month, selectedDate, dateInterval)

      const method =
        selectedTab === EngagementOrgUserReportTypes.TotalEngagement
          ? getTotalEngagementPerOrgUserReport
          : getLoginPerOrgUserReport

      return method({
        ...params,
        params: dateParams,
        filter: extendFilter,
      })
    },
    [
      accessCodeFilterValue,
      dateInterval,
      month,
      rangeType,
      rolesFilterValue,
      cohortsFilterValue,
      selectedDate,
      year,
      selectedTab,
    ],
  )

  const getDataSourceActive = useGetDataSource(getReportWrapper)
  const { onGridReady, gridApi, columnApi } = useGridControl(getDataSourceActive)

  const { tabsOrder, tabOptions, activeTabColumns } = useReportsEngagementOrgUser(selectedTab)

  useRefreshTable({ params: { rangeType, year, month, selectedDate }, gridApi })

  const { csvLoading, onExportCSV } = useExportCSV(
    getReportWrapper,
    gridApi,
    columnApi,
    tabOptions[selectedTab].csvFields,
    tabOptions[selectedTab].csvName,
  )

  return (
    <>
      <TableToolbar
        left={
          <Tabs
            value={engagementType}
            onChange={handleChangeSelectedTab}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons
            sx={{ ml: '-24px' }}
          >
            {tabsOrder.map((type) => (
              <Tab key={type} value={type} label={getTitle(tabOptions[type].title)} />
            ))}
          </Tabs>
        }
        exportCSVEnable
        csvLoading={csvLoading}
        onExportCSV={onExportCSV}
      />

      <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
        <Box minWidth="200px">{RoleFilter}</Box>

        <DateRangeToolbar
          rangeType={rangeType}
          year={year}
          month={month}
          selectedDate={selectedDate}
          dateInterval={dateInterval}
          onChangeDateInterval={onChangeDateInterval}
          onChangeRange={onChangeRange}
          onChangeYear={onChangeYear}
          onChangeMonth={onChangeMonth}
          setSelectedDate={setSelectedDate}
        />

        <Box minWidth="200px">{AccessCodeFilter}</Box>

        <Box minWidth="220px">{CohortsFilter}</Box>
      </Stack>

      <InfinityAgGrid columnDefs={activeTabColumns} pagination onGridReady={onGridReady} />
    </>
  )
}
